(ns app.domain.application.guards.property
  (:require [app.domain.address.model :as address-model]
            [app.domain.group.config.model :as config-model]
            [app.domain.group.permit-type-config.model :as ptc-model]
            [app.domain.property.model :as property-model]
            [app.domain.workspace.model :as workspace-model]
            [clojure.string :as str]
            [core.db.entity.util :as eu]
            [schema-tools.core :as st]
            [schema.core :as s]
            [tilakone.core :as tk]))


(defn no-property-required? [{{:keys [target]} :request}]
  (and (->> target
            :entity/parent
            :workspace/permit-type
            (contains? ptc-model/permit-types-without-mandatory-location))
       (config-model/resolve-boolean :group.config/no-workspace-location (:entity/parent target))))

(defn- has-primary-property? [{{:keys [target]} :request}]
  (-> target :entity/parent :workspace/primary-property some?))

(defn- has-location? [{{:keys [target]} :request}]
  (-> target :entity/parent :entity/location some?))

(def has-property-or-location?
  {:name    :process.guard/has-property-or-location?
   :type    :guard
   :handler (fn [fsm]
              (or (has-primary-property? fsm)
                  (has-location? fsm)))})

(def has-property?
  {:name    :process.guard/has-property?
   :type    :guard
   :handler has-primary-property?})

(def has-no-primary-property?
  {:name    :process.guard/has-no-primary-property?
   :type    :guard
   :handler (complement has-primary-property?)})

(def not-primary-property?
  {:name    :process.guard/not-primary-property?
   :type    :guard
   :handler (fn [{{:keys [target]}          :request
                  [_ {:keys [property-id]}] ::tk/signal}]
              (-> target
                  :entity/parent
                  :workspace/primary-property
                  :db/id
                  (not= property-id)))})

(defn- data-source-matches? [{{:keys [target]} :request} data-source]
  (->> target
       :entity/parent
       :workspace/primary-property
       :property/data-source
       (= data-source)))

(defn- data-source-gis? [fsm]
  (data-source-matches? fsm :property.data-source/gis))

(defn- data-source-gis-or-location? [fsm]
  (if (has-primary-property? fsm)
    (data-source-gis? fsm)
    (has-location? fsm)))

(def primary-property-from-gis?
  {:name    :process.guard/primary-property-from-gis?
   :type    :guard
   :handler data-source-gis?})

(def primary-property-from-gis-or-has-location?
  {:name    :process.guard/primary-property-from-gis-or-has-location?
   :type    :guard
   :handler data-source-gis-or-location?})

(def primary-property-not-from-gis?
  {:name    :process.guard/primary-property-not-from-gis?
   :type    :guard
   :handler (complement data-source-gis?)})

#?(:clj
   (defn- property-owner-search-supported-handler [_]
     ;; Backend actions already no-op when property owner search is not configured;
     ;; this guard only hides frontend affordances while map config is loading or unsupported.
     true)
   :cljs
   (defn- property-owner-search-supported-handler
     [{{:keys [map-config]} :request}]
     (:check-property-owners? map-config)))

(def property-owner-search-supported?
  {:name    :process.guard/property-owner-search-supported?
   :type    :guard
   :handler property-owner-search-supported-handler})

(defn- has-execution? [{{:keys [target]} :request}]
  (->> target :entity/parent workspace-model/workspace-execution some?))

(def primary-property-from-gis-or-execution-exists?
  {:name    :process.guard/primary-property-from-gis-or-execution-exists?
   :type    :guard
   :handler (fn [fsm]
              (or (data-source-gis? fsm)
                  (has-execution? fsm)
                  (no-property-required? fsm)))})

(def primary-property-from-gis-or-execution-exists-or-has-location?
  {:name    :process.guard/primary-property-from-gis-or-execution-exists-or-has-location?
   :type    :guard
   :handler (fn [fsm]
              (or (has-execution? fsm)
                  (data-source-gis-or-location? fsm)))})

(defn- non-blank-attribute-value? [value]
  (if (string? value)
    (-> value str/blank? not)
    (some? value)))

(def primary-property-has-attribute?
  {:name    :process.guard/primary-property-has-attribute?
   :type    :guard
   :handler (fn [{{:keys [target]}           :request
                  [_ attribute error-tr-key] ::tk/guard}]
              (let [has-attribute? (->> target
                                        :entity/parent
                                        :workspace/primary-property
                                        attribute
                                        non-blank-attribute-value?)]
                (if (and error-tr-key (not has-attribute?))
                  (throw (ex-info "Required property attribute missing"
                                  {:main-error-translation-key error-tr-key
                                   :error-translation-keys     [attribute]}))
                  has-attribute?)))})

(defn primary-property-has-required-attributes-handler [{{:keys [target]} :request
                                                         [_ error-tr-key] ::tk/guard
                                                         :as              fsm}]
  (or (no-property-required? fsm)
      (let [property            (-> target :entity/parent :workspace/primary-property)
            required-attributes (property-model/required-property-keys
                                  (-> property
                                      :property/municipality
                                      :municipality/subdivision)
                                  (-> target
                                      :entity/parent
                                      :workspace/permit-type))
            missing-attribute   (->> required-attributes
                                     (remove (fn [attr]
                                               (-> (get property attr)
                                                   non-blank-attribute-value?)))
                                     first)]
        (if (and error-tr-key missing-attribute)
          (throw (ex-info "Required property attribute missing"
                          {:main-error-translation-key error-tr-key
                           :error-translation-keys     [missing-attribute]}))
          (nil? missing-attribute)))))

(def primary-property-has-required-attributes?
  {:name    :process.guard/primary-property-has-required-attributes?
   :type    :guard
   :summary (str "Check that the primary property has the attributes required in that "
                 "subdivision (e.g. legal description in Ontario)")
   :handler primary-property-has-required-attributes-handler})

(def has-location-or-primary-property-has-required-attributes?
  {:name    :process.guard/has-location-or-primary-property-has-required-attributes?
   :type    :guard
   :summary "Check that public works application has either a property or a location"
   :handler (fn [{{{:entity/keys [parent]} :target} :request
                  :as                               fsm}]
              (if (-> parent :workspace/primary-property)
                (primary-property-has-required-attributes-handler fsm)
                (-> parent :entity/location some?)))})

(def ontario-address-checker
  (s/checker
    (st/required-keys address-model/AddressSchema
                      [:address/street-name
                       :address/street-number])))

(def address-defined-if-required
  {:name    :process.guard/address-defined-if-required
   :type    :guard
   :summary "Check that address has the components required by the Ontario MPAC integration"
   :handler (fn [{{:keys [target]} :request :as fsm}]
              (or (-> target :entity/parent :workspace/municipality :municipality/subdivision (not= :CA-ON))
                  (no-property-required? fsm)
                  (and (-> target :entity/parent :workspace/address some?)
                       (-> target
                           :entity/parent
                           :workspace/structural-address
                           eu/->map
                           (st/select-schema address-model/AddressSchema)
                           ontario-address-checker
                           nil?))))})

(def guards
  [has-property?
   has-no-primary-property?
   not-primary-property?
   primary-property-from-gis?
   primary-property-from-gis-or-has-location?
   primary-property-from-gis-or-execution-exists?
   primary-property-from-gis-or-execution-exists-or-has-location?
   primary-property-not-from-gis?
   property-owner-search-supported?
   primary-property-has-attribute?
   primary-property-has-required-attributes?
   has-property-or-location?
   has-location-or-primary-property-has-required-attributes?
   address-defined-if-required])
